import { useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * useSports - Hook to fetch sports list from API
 * Used throughout the app for sport dropdowns
 */
export const useSports = (activeOnly = true) => {
  const [sports, setSports] = useState([]);
  const [sportsWithDetails, setSportsWithDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSports = async () => {
      try {
        // Fetch detailed sports data
        const res = await axios.get(`${API}/sports?active_only=${activeOnly}`);
        const sportsData = res.data.sports || [];
        
        // Set full sports objects with details
        setSportsWithDetails(sportsData);
        
        // Set simple name array for backward compatibility
        setSports(sportsData.map(s => s.name));
        
        setError(null);
      } catch (err) {
        console.error("Error fetching sports:", err);
        setError("Failed to load sports");
        
        // Fallback to default sports if API fails
        const fallbackSports = [
          "Football", "Basketball", "Boys Basketball", "Girls Basketball",
          "Mens Basketball", "Womens Basketball", "Soccer", "Baseball",
          "Softball", "Volleyball", "Wrestling", "Tennis", "Golf",
          "Track & Field", "Swimming", "Lacrosse", "Hockey"
        ];
        setSports(fallbackSports);
      } finally {
        setLoading(false);
      }
    };

    fetchSports();
  }, [activeOnly]);

  return { 
    sports,           // Array of sport names (strings) for dropdowns
    sportsWithDetails, // Array of full sport objects with icon, color, etc.
    loading, 
    error,
    refetch: () => {
      setLoading(true);
      // Re-fetch by toggling effect
    }
  };
};

/**
 * SportSelector - Reusable dropdown component for selecting sports
 * Fetches sports from API and displays with icons
 */
export const SportSelector = ({ 
  value, 
  onChange, 
  label = "Sport *",
  className = "",
  showIcon = true,
  includeAll = false,
  allLabel = "All Sports"
}) => {
  const { sportsWithDetails, loading } = useSports(true);

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
          {label}
        </label>
      )}
      <select 
        value={value} 
        onChange={(e) => onChange(e.target.value)} 
        className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
        disabled={loading}
      >
        {includeAll && <option value="">{allLabel}</option>}
        {sportsWithDetails.map(sport => (
          <option key={sport.id || sport.name} value={sport.name}>
            {showIcon && sport.icon ? `${sport.icon} ` : ''}{sport.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default useSports;
